/**
 * Teacher Tools Service for handling all teacher tool-related API calls
 */

import { defaultErrorHandler as commonErrorHandler, createHeaders as createCommonHeaders, getApiUrls } from './serviceConfig';

/**
 * Creates a configured teacher tools service
 * @param {Object} config - Configuration options
 * @param {string} config.baseUrl - Base URL for the API
 * @param {string} config.environment - Environment ('development' or 'production')
 * @param {Object} config.headers - Additional headers to include in all requests
 * @param {Function} config.onError - Global error handler
 * @param {Function} config.getAuthToken - Function to get the latest auth token (preferred)
 * @param {string} config.authToken - Static authentication token for API requests (not recommended)
 * @returns {Object} The teacher tools service API
 */
export function createTeacherToolsService(config = {}) {
  const DEFAULT_HEADERS = config.headers || {};
  const defaultErrorHandler = config.onError || ((error) => commonErrorHandler('Teacher Tools service', error));
  // Use getAuthToken function if provided, or use static token as fallback
  const getAuthToken = config.getAuthToken || (() => config.authToken);

  /**
   * Helper to create request headers
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Object} Headers object
   */
  const createHeaders = (additionalHeaders = {}) => {
    // Only pass custom auth getter if one was provided to the service
    const customAuthGetter = config.getAuthToken ? getAuthToken : null;
    return createCommonHeaders(
      {
        ...DEFAULT_HEADERS,
        ...additionalHeaders,
      },
      customAuthGetter
    );
  };

  /**
   * Helper to handle API errors
   * @param {Response} response - Fetch Response object
   * @param {string} errorPrefix - Prefix for error message
   * @returns {Promise} JSON response or throws error
   */
  const handleResponse = async (response, errorPrefix = 'API error') => {
    if (!response.ok) {
      let errorMessage = `${errorPrefix}: ${response.status}`;

      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = `${errorPrefix}: ${errorData.message}`;
        }
      } catch {
        // If we can't parse the error JSON, just use the status code
      }

      const error = new Error(errorMessage);
      error.statusCode = response.status;
      throw error;
    }

    return response.json();
  };

  return {
    /**
     * Create a Real World Connection conversation
     * @param {Object} options - Configuration for the real world connection
     * @param {string} options.educationYear - The education year (e.g., "1º ESO")
     * @param {string} options.educationLevel - The education level (e.g., "Secundaria")
     * @param {string} options.textToAdapt - The text content to adapt
     * @param {string} options.additionalNotes - Optional additional notes or requirements
     * @returns {Promise<Object>} Response with conversationId
     */
    createRealWorldConnection: async (options = {}) => {
      try {
        const requestBody = {
          educationYear: options.educationYear,
          educationLevel: options.educationLevel,
          textToAdapt: options.textToAdapt,
        };

        // Include additional notes if provided
        if (options.additionalNotes) {
          requestBody.additionalNotes = options.additionalNotes;
        }

        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.realWorldConnection(), {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        const data = await handleResponse(response, 'Error creating real world connection');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Get lesson content as text
     * @param {string} lessonId - The ID of the lesson to retrieve content for
     * @returns {Promise<Object>} Response with lesson content
     * @throws {Error} 404 if lesson not found
     */
    getLessonContent: async (lessonId) => {
      try {
        const urls = getApiUrls();
        const response = await fetch(`${urls.teacherTools.getLessonContent(lessonId)}`, {
          method: 'GET',
          headers: createHeaders(),
        });

        if (!response.ok) {
          console.error('[TeacherTools] Failed to fetch lesson content:', response.status);
          const error = new Error(`Failed to fetch lesson content: ${response.status}`);
          error.statusCode = response.status;
          throw error;
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error('[TeacherTools] Error fetching lesson content:', error);
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Get teacher tools history with pagination
     * @param {Object} options - Pagination and filter options
     * @param {number} options.limit - Number of items per page (default: 20)
     * @param {number} options.offset - Offset for pagination (default: 0)
     * @returns {Promise<Object>} Response with items, total, limit, and offset
     */
    getHistory: async (options = {}) => {
      try {
        const { limit = 20, offset = 0 } = options;
        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.history({ limit, offset }), {
          method: 'GET',
          headers: createHeaders(),
        });

        const data = await handleResponse(response, 'Error fetching teacher tools history');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Create a Text Level Adapter conversation
     * @param {Object} options - Configuration for the text level adapter
     * @param {string} options.educationYear - The education year (e.g., "1º ESO")
     * @param {string} options.educationLevel - The education level (e.g., "Secundaria")
     * @param {string} options.textToAdapt - The text content to adapt
     * @param {Array<string>} options.specialNeeds - Optional array of special needs
     * @param {string} options.additionalNotes - Optional additional notes or requirements
     * @returns {Promise<Object>} Response with conversationId
     */
    createTextLevelAdapter: async (options = {}) => {
      try {
        const requestBody = {
          educationYear: options.educationYear,
          educationLevel: options.educationLevel,
          textToAdapt: options.textToAdapt,
        };

        // Include special needs if provided
        if (options.specialNeeds) {
          requestBody.specialNeeds = options.specialNeeds;
        }

        // Include additional notes if provided
        if (options.additionalNotes) {
          requestBody.additionalNotes = options.additionalNotes;
        }

        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.textLevelAdapter(), {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        const data = await handleResponse(response, 'Error creating text level adapter');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Create a Vocabulary Creator conversation
     * @param {Object} options - Configuration for the vocabulary creator
     * @param {string} options.educationYear - The education year (e.g., "1º ESO")
     * @param {string} options.educationLevel - The education level (e.g., "Secundaria")
     * @param {string} options.textToAdapt - The text content to extract vocabulary from
     * @param {number} options.numberOfWordsToExtract - Number of vocabulary words to extract (1-50)
     * @param {Array<string>} options.specialNeeds - Optional array of special needs
     * @param {string} options.additionalNotes - Optional additional notes or requirements
     * @returns {Promise<Object>} Response with conversationId
     */
    createVocabularyCreator: async (options = {}) => {
      try {
        const requestBody = {
          educationYear: options.educationYear,
          educationLevel: options.educationLevel,
          textToAdapt: options.textToAdapt,
          numberOfWordsToExtract: options.numberOfWordsToExtract || 10,
        };

        // Include special needs if provided
        if (options.specialNeeds) {
          requestBody.specialNeeds = options.specialNeeds;
        }

        // Include additional notes if provided
        if (options.additionalNotes) {
          requestBody.additionalNotes = options.additionalNotes;
        }

        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.vocabularyCreator(), {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        const data = await handleResponse(response, 'Error creating vocabulary creator');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Create a Text Translator conversation
     * @param {Object} options - Configuration for the text translator
     * @param {string} options.educationYear - The education year (e.g., "1º ESO")
     * @param {string} options.educationLevel - The education level (e.g., "Secundaria")
     * @param {string} options.textToAdapt - The text content to translate
     * @param {string} options.destinationLanguage - ISO language code (e.g., "ca-valencia", "en", "fr")
     * @param {string} options.specialNeeds - Optional special needs (e.g., "Dyslexia, ADHD")
     * @param {string} options.additionalNotes - Optional additional notes or requirements
     * @returns {Promise<Object>} Response with conversationId
     */
    createTextTranslator: async (options = {}) => {
      try {
        const requestBody = {
          educationYear: options.educationYear,
          educationLevel: options.educationLevel,
          textToAdapt: options.textToAdapt,
          destinationLanguage: options.destinationLanguage,
        };

        // Include special needs if provided
        if (options.specialNeeds) {
          requestBody.specialNeeds = options.specialNeeds;
        }

        // Include additional notes if provided
        if (options.additionalNotes) {
          requestBody.additionalNotes = options.additionalNotes;
        }

        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.textTranslator(), {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        const data = await handleResponse(response, 'Error creating text translator');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },
  };
}

// Default export with default configuration
export default createTeacherToolsService();
